import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  Upload,
  Users,
  BarChart2,
  Filter,
  Download,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  Eye,
  GitCompare,
  FileText,
  X,
  Zap,
  AlertCircle,
} from 'lucide-react'
import { Button, IconButton } from '../../components/ui/Button'
import { ScoreBar, ScoreBreakdown, ScoreCircle } from '../../components/ui/ScoreBar'
import { StatusBadge, Badge } from '../../components/ui/Badge'
import { Modal, SlidePanel } from '../../components/ui/Modal'
import { JobDetailsPanel } from '../../components/jobs/JobDetailsPanel'
import { UpgradeModal } from '../../components/shared/UpgradeModal'
import { InlineLoader } from '../../components/ui/LoadingSpinner'
import useJobStore from '../../store/jobStore'
import useAuthStore from '../../store/authStore'
import useOrgStore from '../../store/orgStore'
import { candidatesApi, exportApi, jobsApi, uploadApi, uploadFileToS3 } from '../../lib/api'
import {
  capitalize,
  isValidCVFile,
  formatDateTime,
  formatFileSize,
  canAccessFeature,
} from '../../lib/utils'
import { getEffectivePlan } from '../../lib/subscription'
import { useLocale } from '../../i18n/LocaleProvider'
import toast from 'react-hot-toast'

export function JobDetail() {
  const { jobId, candidateId } = useParams()
  const navigate = useNavigate()
  const { fetchCandidates, candidates, loadingCandidates, updateCandidateStatus } = useJobStore()
  const { user } = useAuthStore()
  const { org, usage, fetchUsage } = useOrgStore()

  const [job, setJob] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedCandidate, setSelectedCandidate] = useState(null)
  const [compareList, setCompareList] = useState([])
  const [showUpload, setShowUpload] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadFiles, setUploadFiles] = useState([])
  const [uploadProgress, setUploadProgress] = useState([])
  const [exporting, setExporting] = useState(false)
  const [sortBy, setSortBy] = useState('score')
  const [sortDir, setSortDir] = useState('desc')
  const [loadingSelectedCandidate, setLoadingSelectedCandidate] = useState(false)
  const { locale, t } = useLocale()

  const plan = getEffectivePlan({ user, org, usage })
  const showDebugScoring = user?.isPlatformAdmin === true

  useEffect(() => {
    loadData()
  }, [jobId])

  useEffect(() => {
    if (!candidateId) {
      setSelectedCandidate(null)
      setLoadingSelectedCandidate(false)
      return
    }

    const listCandidate = candidates.find((item) => item.candidateId === candidateId)
    if (listCandidate) {
      setSelectedCandidate((current) => mergeCandidateDetail(current, listCandidate))
    }

    let cancelled = false

    async function loadCandidateDetail() {
      setLoadingSelectedCandidate(true)
      try {
        const detail = await candidatesApi.getDetail(jobId, candidateId)
        if (!cancelled) {
          setSelectedCandidate((current) =>
            mergeCandidateDetail(current || listCandidate, detail)
          )
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error.message || t('authenticated.jobDetail.toasts.loadCandidateFailed'))
          navigate(`/dashboard/jobs/${jobId}`, { replace: true })
        }
      } finally {
        if (!cancelled) {
          setLoadingSelectedCandidate(false)
        }
      }
    }

    loadCandidateDetail()

    return () => {
      cancelled = true
    }
  }, [candidateId, candidates, jobId, navigate])

  async function loadData() {
    const [jobData, data] = await Promise.all([
      jobsApi.get(jobId).catch(() => null),
      fetchCandidates(jobId),
    ])
    if (jobData) {
      setJob(jobData)
    } else if (data?.jobTitle) {
      setJob({
        jobId,
        title: data.jobTitle,
        description: data.jobDescription,
        requirements: data.requirements || '',
      })
    }
    fetchUsage()
  }

  const onDrop = useCallback((acceptedFiles) => {
    const valid = acceptedFiles.filter(isValidCVFile)
    setUploadFiles(valid)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
  })

  async function handleUpload() {
    if (!uploadFiles.length) return

    if (usage?.canUploadCV === false) {
      setShowUpgradeModal(true)
      return
    }

    setUploading(true)
    const progress = uploadFiles.map((f) => ({ name: f.name, status: 'pending' }))
    setUploadProgress(progress)

    for (let i = 0; i < uploadFiles.length; i++) {
      const file = uploadFiles[i]

      try {
        // Get upload URL
        const urlData = await uploadApi.getUploadUrl(
          jobId,
          file.name,
          file.type
        )

        // Upload to S3
        await uploadFileToS3(urlData.uploadUrl, file)

        // Parse CV
        await uploadApi.parseCV(jobId, urlData.s3Key, file.name, locale)

        progress[i] = { name: file.name, status: 'success' }
        setUploadProgress([...progress])
      } catch (err) {
        progress[i] = {
          name: file.name,
          status: 'error',
          error: err.message || t('authenticated.jobDetail.toasts.uploadFailed'),
          duplicateCandidate: Boolean(err?.duplicateCandidate),
        }
        setUploadProgress([...progress])
        if (err?.duplicateCandidate && err?.message) {
          toast.error(err.message)
        }
      }
    }

    setUploading(false)
    const successCount = progress.filter((p) => p.status === 'success').length
    if (successCount > 0) {
      toast.success(t('authenticated.jobDetail.toasts.processed', {
        count: successCount,
        plural: successCount > 1 ? 's' : '',
      }))
      setTimeout(() => {
        setShowUpload(false)
        setUploadFiles([])
        setUploadProgress([])
        loadData()
      }, 1500)
    }
  }

  async function handleStatusUpdate(candidateId, status) {
    const result = await updateCandidateStatus(jobId, candidateId, status)
    if (result.success) {
      toast.success(t('authenticated.jobDetail.toasts.candidateStatus', {
        status: t(`authenticated.badges.status.${status}`),
      }))
      if (selectedCandidate?.candidateId === candidateId) {
        setSelectedCandidate((prev) => ({ ...prev, status }))
      }
    } else {
      toast.error(result.error || t('authenticated.jobDetail.toasts.updateStatusFailed'))
    }
  }

  async function handleExport(scope) {
    if (!canAccessFeature(plan, 'export')) {
      setShowUpgradeModal(true)
      return
    }
    setExporting(true)
    try {
      const candidateIds =
        scope === 'selected' ? compareList.map((candidate) => candidate.candidateId) : []
      const data = await exportApi.generate(jobId, 'CSV', {
        scope,
        candidateIds,
      })
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank')
        if (scope === 'all') {
          toast.success(t('authenticated.jobDetail.toasts.exportAll', { count: data.candidateCount }))
        } else if (scope === 'shortlisted') {
          toast.success(t('authenticated.jobDetail.toasts.exportShortlisted', { count: data.candidateCount }))
        } else {
          toast.success(t('authenticated.jobDetail.toasts.exportSelected', { count: data.candidateCount }))
        }
      }
    } catch (err) {
      toast.error(err.message || t('authenticated.jobDetail.toasts.exportFailed'))
    }
    setExporting(false)
    setShowExportModal(false)
  }

  async function handleRequirementsSave({ requirements, rescoreCandidates }) {
    try {
      const result = await jobsApi.update(jobId, {
        requirements,
        rescoreCandidates,
        locale,
      })
      if (result?.job) {
        setJob(result.job)
      }
      await loadData()
      toast.success(
        rescoreCandidates
          ? t('authenticated.jobDetail.toasts.requirementsSavedRescore')
          : t('authenticated.jobDetail.toasts.requirementsSaved')
      )
      if (result?.rescore && result.rescore.success === false) {
        toast.error(result.rescore.error || t('authenticated.jobDetail.toasts.rescoreWarning'))
      }
      return result
    } catch (err) {
      toast.error(err.message || t('authenticated.jobDetail.toasts.requirementsFailed'))
      throw err
    }
  }

  function openCandidateDetail(candidate) {
    if (!candidate?.candidateId) return
    setSelectedCandidate((current) => mergeCandidateDetail(current, candidate))
    navigate(`/dashboard/jobs/${jobId}/candidates/${candidate.candidateId}`)
  }

  function closeCandidateDetail() {
    setSelectedCandidate(null)
    setLoadingSelectedCandidate(false)
    navigate(`/dashboard/jobs/${jobId}`)
  }

  function toggleCompare(candidate) {
    if (compareList.find((c) => c.candidateId === candidate.candidateId)) {
      setCompareList(compareList.filter((c) => c.candidateId !== candidate.candidateId))
    } else {
      if (compareList.length >= 4) {
        toast.error(t('authenticated.jobDetail.toasts.compareLimit'))
        return
      }
      if (!canAccessFeature(plan, 'compare')) {
        setShowUpgradeModal(true)
        return
      }
      setCompareList([...compareList, candidate])
    }
  }

  function openComparison() {
    const candidateIds = compareList.map((candidate) => candidate.candidateId).filter(Boolean)
    if (candidateIds.length < 2) {
      toast.error(t('authenticated.jobDetail.toasts.compareMinimum'))
      return
    }

    navigate(`/dashboard/jobs/${jobId}/compare?ids=${candidateIds.join(',')}`, {
      state: { candidateIds, candidates: compareList },
    })
  }

  function sortCandidates(list) {
    return [...list].sort((a, b) => {
      let aVal, bVal
      if (sortBy === 'score') {
        aVal = a.matchScore || 0
        bVal = b.matchScore || 0
      } else if (sortBy === 'name') {
        aVal = a.name || ''
        bVal = b.name || ''
        return sortDir === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      } else if (sortBy === 'experience') {
        aVal = a.experienceYears || 0
        bVal = b.experienceYears || 0
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal
    })
  }

  function handleSort(field) {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortDir('desc')
    }
  }

  const filtered = candidates.filter((c) => {
    if (statusFilter === 'all') return true
    return c.status === statusFilter
  })

  const sorted = sortCandidates(filtered)

  const scoreBands = {
    excellent: candidates.filter((c) => c.matchScore >= 90).length,
    good: candidates.filter((c) => c.matchScore >= 70 && c.matchScore < 90).length,
    partial: candidates.filter((c) => c.matchScore >= 50 && c.matchScore < 70).length,
    poor: candidates.filter((c) => c.matchScore < 50).length,
  }

  const SortIcon = ({ field }) => {
    if (sortBy !== field)
      return <ChevronUp className="w-3 h-3 text-gray-300" />
    return sortDir === 'asc' ? (
      <ChevronUp className="w-3 h-3 text-brand-600" />
    ) : (
      <ChevronDown className="w-3 h-3 text-brand-600" />
    )
  }

  return (
    <div className="animate-fade-in">
      {/* Back */}
      <button
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-5 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('authenticated.jobDetail.backToJobs')}
      </button>

      {/* Job header */}
      <div className="card p-5 mb-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900 mb-1">
              {job?.title || t('authenticated.jobDetail.loadingJob')}
            </h1>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {t('authenticated.jobDetail.candidatesCount', {
                  count: candidates.length,
                  plural: candidates.length === 1 ? '' : 's',
                })}
              </span>
              {candidates.length > 0 && (
                <span className="flex items-center gap-1">
                  <BarChart2 className="w-4 h-4" />
                  {t('authenticated.jobDetail.topScore', {
                    score: Math.max(...candidates.map((c) => c.matchScore || 0)),
                  })}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => loadData()}
              icon={<RefreshCw className="w-4 h-4" />}
            >
              {t('authenticated.jobDetail.refresh')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              loading={exporting}
              onClick={() => setShowExportModal(true)}
              icon={<Download className="w-4 h-4" />}
            >
              {t('authenticated.jobDetail.export')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowUpload(true)}
              icon={<Upload className="w-4 h-4" />}
            >
              {t('authenticated.jobDetail.uploadCvs')}
            </Button>
          </div>
        </div>

        {/* Score bands */}
        {candidates.length > 0 && (
          <div className="grid grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-100">
            {[
              { label: t('authenticated.jobDetail.scoreBands.excellent'), count: scoreBands.excellent, color: 'text-green-600 bg-green-50' },
              { label: t('authenticated.jobDetail.scoreBands.good'), count: scoreBands.good, color: 'text-blue-600 bg-blue-50' },
              { label: t('authenticated.jobDetail.scoreBands.partial'), count: scoreBands.partial, color: 'text-yellow-600 bg-yellow-50' },
              { label: t('authenticated.jobDetail.scoreBands.poor'), count: scoreBands.poor, color: 'text-red-600 bg-red-50' },
            ].map((band) => (
              <div
                key={band.label}
                className={`rounded-xl p-3 text-center ${band.color}`}
              >
                <p className="text-2xl font-bold">{band.count}</p>
                <p className="text-xs font-medium">{band.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mb-5">
        <JobDetailsPanel job={job} onSaveRequirements={handleRequirementsSave} />
      </div>

      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title={t('authenticated.jobDetail.exportTitle')}
        subtitle={t('authenticated.jobDetail.exportSubtitle')}
        size="sm"
      >
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            {t('authenticated.jobDetail.exportDescription')}
          </p>
          <div className="space-y-2">
            <Button
              variant="primary"
              fullWidth
              loading={exporting}
              disabled={compareList.length === 0}
              onClick={() => handleExport('selected')}
            >
              {t('authenticated.jobDetail.exportSelected', { count: compareList.length })}
            </Button>
            <Button
              variant="secondary"
              fullWidth
              loading={exporting}
              disabled={candidates.filter((candidate) => candidate.status === 'shortlisted').length === 0}
              onClick={() => handleExport('shortlisted')}
            >
              {t('authenticated.jobDetail.exportShortlisted', {
                count: candidates.filter((candidate) => candidate.status === 'shortlisted').length,
              })}
            </Button>
            <Button
              variant="secondary"
              fullWidth
              loading={exporting}
              onClick={() => handleExport('all')}
            >
              {t('authenticated.jobDetail.exportAll', { count: candidates.length })}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Compare bar */}
      <AnimatePresence>
        {compareList.length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="card p-4 mb-5 bg-brand-50 border-brand-200 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <GitCompare className="w-5 h-5 text-brand-600" />
              <span className="text-sm font-medium text-brand-900">
                {t('authenticated.jobDetail.compareSelected', { count: compareList.length })}
              </span>
              <div className="flex gap-2">
                {compareList.map((c) => (
                  <span
                    key={c.candidateId}
                    className="text-xs bg-white text-brand-700 px-2.5 py-1 rounded-full border border-brand-200"
                  >
                    {c.name}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={openComparison}
              >
                {t('authenticated.jobDetail.compareNow')}
              </Button>
              <IconButton
                icon={<X className="w-4 h-4" />}
                label={t('authenticated.jobDetail.clear')}
                onClick={() => setCompareList([])}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filters */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            {['all', 'shortlisted', 'pending', 'rejected'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize ${
                  statusFilter === s
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t(`authenticated.jobDetail.filters.${s}`)}
                {s === 'shortlisted' && (
                  <span className="ml-1 text-green-600">
                    ({candidates.filter((c) => c.status === 'shortlisted').length})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        <span className="text-sm text-gray-500">
          {t('authenticated.jobDetail.candidateTotal', {
            count: sorted.length,
            plural: sorted.length === 1 ? '' : 's',
          })}
        </span>
      </div>

      {/* Candidates */}
      {loadingCandidates ? (
        <InlineLoader text={t('authenticated.jobDetail.loadingCandidates')} />
      ) : sorted.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="empty-state-title">{t('authenticated.jobDetail.noCandidates')}</h3>
            <p className="empty-state-description">
              {t('authenticated.jobDetail.noCandidatesDescription')}
            </p>
            <Button
              variant="primary"
              className="mt-4"
              onClick={() => setShowUpload(true)}
              icon={<Upload className="w-4 h-4" />}
            >
              {t('authenticated.jobDetail.uploadCvs')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-100">
            <div className="col-span-1 table-header">#</div>
            <button
              className="col-span-3 table-header text-left flex items-center gap-1 hover:text-gray-700"
              onClick={() => handleSort('name')}
            >
              {t('authenticated.jobDetail.table.candidate')} <SortIcon field="name" />
            </button>
            <button
              className="col-span-2 table-header text-left flex items-center gap-1 hover:text-gray-700"
              onClick={() => handleSort('score')}
            >
              {t('authenticated.jobDetail.table.score')} <SortIcon field="score" />
            </button>
            <button
              className="col-span-2 table-header text-left flex items-center gap-1 hover:text-gray-700"
              onClick={() => handleSort('experience')}
            >
              {t('authenticated.jobDetail.table.experience')} <SortIcon field="experience" />
            </button>
            <div className="col-span-2 table-header">{t('authenticated.jobDetail.table.skills')}</div>
            <div className="col-span-2 table-header">{t('authenticated.jobDetail.table.actions')}</div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-gray-50">
            {sorted.map((candidate, index) => (
              <motion.div
                key={candidate.candidateId}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.03 }}
                className={`grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-gray-50 transition-colors ${
                  candidate.status === 'shortlisted' ? 'bg-green-50' : ''
                }`}
              >
                <div className="col-span-1 text-sm font-bold text-gray-400">
                  #{index + 1}
                </div>

                <div
                  className="col-span-3 cursor-pointer"
                  onClick={() => openCandidateDetail(candidate)}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-brand-700">
                        {candidate.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {candidate.name || t('authenticated.jobDetail.unknownCandidate')}
                      </p>
                      {candidate.status === 'shortlisted' && (
                        <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                          <Star className="w-3 h-3 fill-green-500 text-green-500" />
                          {t('authenticated.jobDetail.shortlisted')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-span-2">
                  <ScoreBar score={candidate.matchScore || 0} size="sm" />
                </div>

                <div className="col-span-2 text-sm text-gray-600">
                  {candidate.experienceYears
                    ? t('authenticated.jobDetail.yearsShort', {
                        count: candidate.experienceYears,
                        plural: candidate.experienceYears === 1 ? '' : 's',
                      })
                    : '-'}
                </div>

                <div className="col-span-2">
                  <div className="flex flex-wrap gap-1">
                    {(candidate.skills || []).slice(0, 2).map((skill) => (
                      <span
                        key={skill}
                        className="text-xs bg-brand-50 text-brand-700 px-2 py-0.5 rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                    {(candidate.skills || []).length > 2 && (
                      <span className="text-xs text-gray-400">
                        +{candidate.skills.length - 2}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-span-2 flex items-center gap-1">
                  <IconButton
                    icon={<Eye className="w-4 h-4" />}
                    label={t('authenticated.jobDetail.view')}
                    variant="ghost"
                    onClick={() => openCandidateDetail(candidate)}
                  />
                  <IconButton
                    icon={
                      <Star
                        className={`w-4 h-4 ${
                          candidate.status === 'shortlisted'
                            ? 'fill-yellow-400 text-yellow-400'
                            : ''
                        }`}
                      />
                    }
                    label={t('authenticated.jobDetail.shortlist')}
                    variant="ghost"
                    onClick={() =>
                      handleStatusUpdate(
                        candidate.candidateId,
                        candidate.status === 'shortlisted'
                          ? 'pending'
                          : 'shortlisted'
                      )
                    }
                  />
                  <IconButton
                    icon={
                      <GitCompare
                        className={`w-4 h-4 ${
                          compareList.find(
                            (c) => c.candidateId === candidate.candidateId
                          )
                            ? 'text-brand-600'
                            : ''
                        }`}
                      />
                    }
                    label={t('authenticated.jobDetail.compare')}
                    variant="ghost"
                    onClick={() => toggleCompare(candidate)}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Candidate detail panel */}
      <SlidePanel
        isOpen={Boolean(candidateId)}
        onClose={closeCandidateDetail}
        title={selectedCandidate?.name || t('authenticated.jobDetail.candidateFallback')}
        subtitle={
          loadingSelectedCandidate && !selectedCandidate
            ? t('authenticated.jobDetail.loadingCandidateDetails')
            : t('authenticated.jobDetail.matchScore', { score: selectedCandidate?.matchScore || 0 })
        }
      >
        {loadingSelectedCandidate && !selectedCandidate ? (
          <InlineLoader text={t('authenticated.jobDetail.loadingCandidateDetails')} />
        ) : selectedCandidate ? (
          <CandidateDetailPanel
            candidate={selectedCandidate}
            onStatusUpdate={handleStatusUpdate}
            onCompare={() => {
              toggleCompare(selectedCandidate)
              closeCandidateDetail()
            }}
            isInCompare={compareList.some(
              (c) => c.candidateId === selectedCandidate.candidateId
            )}
            showDebug={showDebugScoring}
          />
        ) : (
          <p className="text-sm text-gray-500">{t('authenticated.jobDetail.candidateDetailsFailed')}</p>
        )}
      </SlidePanel>

      {/* Upload panel */}
      <SlidePanel
        isOpen={showUpload}
        onClose={() => {
          setShowUpload(false)
          setUploadFiles([])
          setUploadProgress([])
        }}
        title={t('authenticated.jobDetail.uploadTitle')}
        subtitle={t('authenticated.jobDetail.supportedFiles')}
      >
        <div className="space-y-4">
          {usage?.canUploadCV === false ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-yellow-900 text-sm">
                    {t('authenticated.jobDetail.limitReached')}
                  </p>
                  <p className="text-yellow-700 text-sm mt-1">
                    {t('authenticated.jobDetail.limitReachedDescription')}
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      setShowUpload(false)
                      setShowUpgradeModal(true)
                    }}
                  >
                    {t('common.upgradePlan')}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  isDragActive
                    ? 'border-brand-400 bg-brand-50'
                    : 'border-gray-300 hover:border-brand-400 hover:bg-brand-50'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-700 font-medium mb-1">
                  {t('authenticated.jobDetail.dropOrBrowse')}
                </p>
                <p className="text-gray-400 text-sm">{t('authenticated.jobDetail.supportedFiles')}</p>
              </div>

              {uploadFiles.length > 0 && (
                <div className="space-y-2">
                  {uploadFiles.map((file, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2.5"
                    >
                      <FileText className="w-4 h-4 text-brand-600 shrink-0" />
                      <span className="text-sm text-gray-700 flex-1 truncate">
                        {file.name}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {uploadProgress.length > 0 && (
                <div className="space-y-2">
                  {uploadProgress.map((p, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2.5"
                    >
                      {p.status === 'success' ? (
                        <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                      ) : p.status === 'error' ? (
                        <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                      ) : (
                        <Clock className="w-4 h-4 text-gray-400 shrink-0 animate-spin" />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="block text-sm text-gray-700 truncate">
                          {p.name}
                        </span>
                        {p.status === 'error' && p.error && (
                          <span className="block text-xs text-red-600 mt-0.5">
                            {p.error}
                          </span>
                        )}
                      </div>
                      <span
                        className={`text-xs font-medium ${
                          p.status === 'success'
                            ? 'text-green-600'
                            : p.status === 'error'
                            ? 'text-red-600'
                            : 'text-gray-400'
                        }`}
                      >
                        {p.status === 'pending'
                          ? t('authenticated.jobDetail.processing')
                          : p.status === 'error'
                          ? p.duplicateCandidate
                            ? t('authenticated.jobDetail.duplicate')
                            : t('authenticated.jobDetail.error')
                          : t('authenticated.jobDetail.done')}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <Button
                variant="primary"
                fullWidth
                loading={uploading}
                disabled={uploadFiles.length === 0}
                onClick={handleUpload}
                icon={<Zap className="w-4 h-4" />}
              >
                {uploadFiles.length > 0
                  ? t('authenticated.jobDetail.processWithAi', {
                      count: uploadFiles.length,
                      plural: uploadFiles.length === 1 ? '' : 's',
                    })
                  : t('authenticated.jobDetail.processNoCount')}
              </Button>
            </>
          )}
        </div>
      </SlidePanel>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  )
}

function CandidateDetailPanel({
  candidate,
  onStatusUpdate,
  onCompare,
  isInCompare,
  showDebug = false,
}) {
  const { t } = useLocale()
  const recommendation = getCandidateRecommendation(candidate)
  const recommendationVariant = getRecommendationVariant(recommendation)
  const aiEvaluation = getCandidateAiEvaluation(candidate)
  const matchedEvidence = getCandidateMatchedEvidence(candidate)
  const missingEvidence = getCandidateMissingEvidence(candidate, matchedEvidence)
  const cvDetails = buildCandidateCvDetails(candidate, t)
  const workHistory = Array.isArray(candidate.workHistory) ? candidate.workHistory : []

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-4">
        <ScoreCircle score={candidate.matchScore || 0} size="xl" />
        <div className="min-w-0 flex-1">
          <p className="text-2xl font-bold text-gray-900">
            {candidate.matchScore || 0}
            <span className="text-base font-normal text-gray-400">/100</span>
          </p>
          <p className="text-sm text-gray-500">{t('authenticated.jobDetail.detail.aiMatchScore')}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge variant={recommendationVariant}>
              {t('authenticated.jobDetail.detail.recommendation', {
                label: formatRecommendationLabel(recommendation, t),
              })}
            </Badge>
            <StatusBadge status={candidate.status} />
            {candidate.modelRecommendation &&
              candidate.modelRecommendation !== recommendation && (
                <Badge variant="gray">
                  {t('authenticated.jobDetail.detail.model', {
                    label: formatRecommendationLabel(candidate.modelRecommendation, t),
                  })}
                </Badge>
              )}
          </div>
        </div>
      </div>

      {candidate.summary && (
        <div className="bg-brand-50 rounded-xl p-4 border border-brand-100">
          <p className="text-xs font-semibold text-brand-700 mb-1">{t('authenticated.jobDetail.detail.summary')}</p>
          <p className="text-sm text-brand-800 leading-relaxed">{candidate.summary}</p>
        </div>
      )}

      {(candidate.whyThisScore || candidate.whyThisMatch) && (
        <div className="bg-gray-50 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-500 mb-1">{t('authenticated.jobDetail.detail.whyScore')}</p>
          <p className="text-sm text-gray-700">
            {candidate.whyThisScore || candidate.whyThisMatch}
          </p>
        </div>
      )}

      {candidate.scoreBreakdown && (
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-xs font-semibold text-gray-500 mb-3">{t('authenticated.jobDetail.detail.scoreBreakdown')}</p>
          <ScoreBreakdown candidate={candidate} showDebug={showDebug} />
        </div>
      )}

      {matchedEvidence.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-2">{t('authenticated.jobDetail.detail.matchedEvidence')}</p>
          <div className="flex flex-wrap gap-1.5">
            {matchedEvidence.map((item) => (
              <span
                key={item}
                className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      )}

      {candidate.bestMatchingChunkPreview && (
        <div className="rounded-xl border border-green-100 bg-green-50 p-4">
          <p className="text-xs font-semibold text-green-700 mb-1">{t('authenticated.jobDetail.detail.matchedExcerpt')}</p>
          <p className="text-sm text-green-900 whitespace-pre-wrap">
            {candidate.bestMatchingChunkPreview}
          </p>
        </div>
      )}

      {missingEvidence.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-2">{t('authenticated.jobDetail.detail.missingEvidence')}</p>
          <div className="flex flex-wrap gap-1.5">
            {missingEvidence.map((item) => (
              <span
                key={item}
                className="text-xs bg-red-100 text-red-700 px-2.5 py-1 rounded-full"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      )}

      {aiEvaluation?.redFlags?.length > 0 && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-4">
          <p className="text-xs font-semibold text-red-700 mb-2">{t('authenticated.jobDetail.detail.redFlags')}</p>
          <ul className="space-y-1">
            {aiEvaluation.redFlags.map((flag) => (
              <li key={flag} className="text-sm text-red-800">
                {flag}
              </li>
            ))}
          </ul>
        </div>
      )}

      {cvDetails.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-2">{t('authenticated.jobDetail.detail.cvDetails')}</p>
          <div className="grid grid-cols-2 gap-3">
            {cvDetails.map((item) => (
              <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-500">{item.label}</p>
                <p className="font-semibold text-gray-900 text-sm break-words">
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {workHistory.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-2">{t('authenticated.jobDetail.detail.workHistory')}</p>
          <div className="space-y-2">
            {workHistory.map((item, index) => (
              <div key={`${item.role}-${item.company}-${index}`} className="bg-gray-50 rounded-xl p-3">
                <p className="font-semibold text-gray-900 text-sm">
                  {item.role && item.company
                    ? t('authenticated.jobDetail.detail.workHistoryAt', {
                        role: item.role,
                        company: item.company,
                      })
                    : [item.role, item.company].filter(Boolean).join(' - ') ||
                      t('authenticated.jobDetail.detail.previousRole')}
                </p>
                {item.years > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {t('authenticated.jobDetail.detail.years', {
                      count: item.years,
                      plural: item.years === 1 ? '' : 's',
                    })}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {candidate.strengths?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-2">{t('authenticated.jobDetail.detail.strengths')}</p>
          <ul className="space-y-1">
            {candidate.strengths.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="section-divider" />
      <div className="space-y-2">
        <Button
          variant={candidate.status === 'shortlisted' ? 'success' : 'secondary'}
          fullWidth
          onClick={() =>
            onStatusUpdate(
              candidate.candidateId,
              candidate.status === 'shortlisted' ? 'pending' : 'shortlisted'
            )
          }
          icon={<Star className={`w-4 h-4 ${candidate.status === 'shortlisted' ? 'fill-white' : ''}`} />}
        >
          {candidate.status === 'shortlisted'
            ? t('authenticated.jobDetail.detail.removeShortlist')
            : t('authenticated.jobDetail.detail.addShortlist')}
        </Button>
        <Button
          variant={candidate.status === 'rejected' ? 'danger' : 'ghost'}
          fullWidth
          onClick={() =>
            onStatusUpdate(
              candidate.candidateId,
              candidate.status === 'rejected' ? 'pending' : 'rejected'
            )
          }
          icon={<XCircle className="w-4 h-4" />}
        >
          {candidate.status === 'rejected'
            ? t('authenticated.jobDetail.detail.unreject')
            : t('authenticated.jobDetail.detail.reject')}
        </Button>
        <Button
          variant={isInCompare ? 'outline' : 'ghost'}
          fullWidth
          onClick={onCompare}
          icon={<GitCompare className="w-4 h-4" />}
        >
          {isInCompare
            ? t('authenticated.jobDetail.detail.removeCompare')
            : t('authenticated.jobDetail.detail.addCompare')}
        </Button>
      </div>
    </div>
  )
}

function mergeCandidateDetail(current, next) {
  if (!current) return next || null
  if (!next) return current

  return {
    ...current,
    ...next,
    skills: next.skills?.length ? next.skills : current.skills || [],
    missingSkills: next.missingSkills?.length ? next.missingSkills : current.missingSkills || [],
    matchedTerms: next.matchedTerms?.length ? next.matchedTerms : current.matchedTerms || [],
    workHistory: next.workHistory?.length ? next.workHistory : current.workHistory || [],
    educationItems:
      next.educationItems?.length ? next.educationItems : current.educationItems || [],
    certifications:
      next.certifications?.length ? next.certifications : current.certifications || [],
    scoreBreakdown: next.scoreBreakdown || current.scoreBreakdown || null,
    aiEvaluation: next.aiEvaluation || current.aiEvaluation || null,
    summary: next.summary || current.summary || '',
    whyThisScore: next.whyThisScore || current.whyThisScore || '',
    bestMatchingChunkPreview:
      next.bestMatchingChunkPreview || current.bestMatchingChunkPreview || '',
  }
}

function getCandidateAiEvaluation(candidate) {
  return (
    candidate.aiEvaluation ||
    candidate.ai_evaluation ||
    candidate.scoreBreakdown?.aiEvaluation ||
    candidate.scoreBreakdown?.ai_evaluation ||
    null
  )
}

function getCandidateRecommendation(candidate) {
  const aiEvaluation = getCandidateAiEvaluation(candidate)
  return String(
    candidate.recommendation ||
      aiEvaluation?.recommendation ||
      candidate.modelRecommendation ||
      aiEvaluation?.modelRecommendation ||
      'partial'
  )
    .trim()
    .toLowerCase()
}

function getRecommendationVariant(recommendation) {
  if (recommendation === 'strong') return 'green'
  if (recommendation === 'good') return 'blue'
  if (recommendation === 'partial') return 'yellow'
  if (recommendation === 'weak') return 'red'
  return 'gray'
}

function formatRecommendationLabel(recommendation, t) {
  const normalized = String(recommendation || '').trim().toLowerCase()
  if (['strong', 'good', 'partial', 'weak'].includes(normalized)) {
    return t(`authenticated.recommendations.${normalized}`)
  }
  return capitalize(normalized || 'partial')
}

function normaliseDisplayList(values) {
  if (!Array.isArray(values)) return []
  return [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))]
}

function getCandidateMatchedEvidence(candidate) {
  const breakdown = candidate.scoreBreakdown || candidate.score_breakdown || {}
  const mustHave = breakdown.mustHave || breakdown.must_have || {}
  const skillMatch = breakdown.skillMatch || breakdown.skill_match || {}
  const experience = breakdown.experience || {}

  return normaliseDisplayList([
    ...(candidate.matchedTerms || []),
    ...(candidate.skills || []),
    ...(mustHave.matchedTerms || mustHave.matched_terms || []),
    ...(mustHave.supportingTerms || mustHave.supporting_terms || []),
    ...(skillMatch.matchedTerms || skillMatch.matched_terms || []),
    ...(experience.experienceEvidence || experience.experience_evidence || []),
    ...(experience.detected_experience_evidence || []),
  ]).slice(0, 16)
}

function getCandidateMissingEvidence(candidate, matchedEvidence) {
  const breakdown = candidate.scoreBreakdown || candidate.score_breakdown || {}
  const mustHave = breakdown.mustHave || breakdown.must_have || {}
  const matchedSet = new Set(
    normaliseDisplayList(matchedEvidence).map((value) => value.toLowerCase())
  )
  const missingTerms = normaliseDisplayList(
    mustHave.extractedTerms || mustHave.extracted_terms || []
  ).filter((value) => !matchedSet.has(value.toLowerCase()))

  return normaliseDisplayList([
    ...(candidate.missingSkills || []),
    ...missingTerms,
    ...(candidate.redFlags || []),
  ]).slice(0, 16)
}

function buildCandidateCvDetails(candidate, t) {
  const details = [
    candidate.currentRole
      ? { label: t('authenticated.jobDetail.detail.currentRole'), value: candidate.currentRole }
      : null,
    candidate.email ? { label: t('authenticated.jobDetail.detail.email'), value: candidate.email } : null,
    candidate.phone ? { label: t('authenticated.jobDetail.detail.phone'), value: candidate.phone } : null,
    candidate.experienceYears > 0
      ? {
          label: t('authenticated.jobDetail.detail.experience'),
          value: t('authenticated.jobDetail.detail.years', {
            count: candidate.experienceYears,
            plural: candidate.experienceYears === 1 ? '' : 's',
          }),
        }
      : null,
    candidate.education
      ? { label: t('authenticated.jobDetail.detail.education'), value: candidate.education }
      : null,
    candidate.certifications?.length
      ? { label: t('authenticated.jobDetail.detail.certifications'), value: candidate.certifications.join(', ') }
      : null,
    candidate.sourceFileName
      ? { label: t('authenticated.jobDetail.detail.cvFile'), value: candidate.sourceFileName }
      : null,
    candidate.updatedAt
      ? { label: t('authenticated.jobDetail.detail.lastUpdated'), value: formatDateTime(candidate.updatedAt) || candidate.updatedAt }
      : null,
  ]

  return details.filter(Boolean)
}
